import React, { useEffect, useState } from "react";
import {
	Box,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	LinearProgress,
	TextField,
	Typography,
} from "@mui/material";
import { addMinutes } from "date-fns";
import { useTranslation } from "react-i18next";

import AddDialogStyle from "styles/application/AddDialogStyle";
import { localisedDate } from "../hooks/useGanttScale";
import { getServerDates } from "../hooks/useGanttReschedule";
import { toTimeInputValue, withTimeOfDay } from "../utils/dateUtils";
import { getServiceTimeBreakdown } from "../utils/serviceTime";
import { minutesToHours } from "../utils/formatUtils";

const AT = AddDialogStyle();

const formatDateTime = localisedDate({
	weekday: "short",
	day: "numeric",
	month: "short",
	year: "numeric",
	hour: "2-digit",
	minute: "2-digit",
});

const formatDate = localisedDate({
	weekday: "short",
	day: "numeric",
	month: "short",
	year: "numeric",
});

const rowStyles = {
	display: "grid",
	gridTemplateColumns: "110px 1fr",
	alignItems: "center",
	columnGap: "16px",
	minHeight: 36,
};

const labelStyles = {
	fontFamily: "Roboto Condensed",
	fontWeight: "bold",
	fontSize: 14,
};

const valueStyles = {
	fontFamily: "Roboto Condensed",
	fontSize: 15,
};

const mutedValueStyles = {
	...valueStyles,
	color: "text.secondary",
};

const timeInputStyles = {
	width: 140,
	"& .MuiInputBase-input": { padding: "8px 12px" },
};

const Row = ({ label, children }) => (
	<Box sx={rowStyles}>
		<Typography sx={labelStyles}>{label}</Typography>
		{children}
	</Box>
);

// Dropping a bar only lands it on the hour, so the time is confirmed here
// before the new date is saved. Only the start is editable: the duration is
// the service's estimate, so the end simply follows.
const GanttRescheduleDialog = ({ pending, onConfirm, onCancel }) => {
	const { t } = useTranslation();
	const [time, setTime] = useState("");
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		setTime(pending ? toTimeInputValue(pending.start) : "");
	}, [pending]);

	if (!pending) return null;

	const previousStart = getServerDates(pending.service)?.start;
	const durationMinutes = getServiceTimeBreakdown(pending.service).total;
	const start = withTimeOfDay(pending.start, time);
	const end = addMinutes(start, durationMinutes);

	const handleConfirm = async () => {
		setSaving(true);
		try {
			await onConfirm(start);
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog open onClose={saving ? undefined : onCancel} maxWidth="xs" fullWidth>
			{saving && <LinearProgress />}
			<DialogTitle>
				<AT.HeaderText>{t("label.rescheduleService")}</AT.HeaderText>
				<Typography variant="body2" color="text.secondary">
					{pending.workOrder} · {pending.role}
				</Typography>
			</DialogTitle>

			<DialogContent>
				{previousStart && (
					<Row label={t("label.from")}>
						<Typography sx={mutedValueStyles}>
							{formatDateTime(previousStart)}
						</Typography>
					</Row>
				)}

				<Row label={t("label.startTime")}>
					<Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
						<Typography sx={valueStyles}>{formatDate(pending.start)}</Typography>
						<TextField
							name="startTime"
							type="time"
							size="small"
							value={time}
							onChange={(e) => setTime(e.target.value)}
							disabled={saving}
							sx={timeInputStyles}
						/>
					</Box>
				</Row>

				<Row label={t("label.duration")}>
					<Typography sx={mutedValueStyles}>
						{t("label.durationHours", { count: minutesToHours(durationMinutes) })}
					</Typography>
				</Row>

				<Row label={t("label.endTime")}>
					<Typography sx={mutedValueStyles}>{formatDateTime(end)}</Typography>
				</Row>
			</DialogContent>

			<DialogActions>
				<AT.CancelButton onClick={onCancel} variant="contained" disabled={saving}>
					{t("action.cancel")}
				</AT.CancelButton>
				<AT.ConfirmButton
					onClick={handleConfirm}
					variant="contained"
					disabled={saving || !time}
				>
					{t("action.confirm")}
				</AT.ConfirmButton>
			</DialogActions>
		</Dialog>
	);
};

export default GanttRescheduleDialog;
